import { useCallback, useEffect, useRef, useState } from 'react';
import {
  BfIconChevDown,
  BfIconSearch,
  BfIconBookmark,
  BfIconMenu,
} from './BfIcons';
import { isTelegramShell } from '../../../services/telegram';
import { t } from '../../../i18n/uiText';

/**
 * BfHeader — floating compact header.
 *  ┌─ Иоанн · 3 ⌃ ───── 🔍 🔖 ⋯ ─┐
 * Скрывается по скроллу вниз, проявляется по скроллу вверх.
 * Внутри показывает hairline только когда страница прокручена.
 */
export default function BfHeader({
  bookName,
  chapter,
  hidden = false,
  scrolled = false,
  onOpenChapterSelector,
  onOpenSearch,
  onOpenBookmarks,
  onOpenMenu,
  hasBookmark = false,
  langSlot = null,
  translationSlot = null,
}) {
  const brandMode = useBfHeaderBrandMode();

  return (
    <header
      className="bf-header"
      data-hidden={hidden ? 'true' : 'false'}
      data-scrolled={scrolled ? 'true' : 'false'}
    >
      {brandMode && (
        <div
          className={`bf-header__tg-brand${
            brandMode === 'tg' ? ' bf-header__tg-brand--tg' : ''
          }${brandMode === 'center' ? ' bf-header__tg-brand--center' : ''}`}
          aria-label="BibleFirst.co"
        >
          <span className="bf-header__tg-brand-text" lang="en">BibleFirst</span>
          <span className="bf-header__tg-brand-tld">.co</span>
        </div>
      )}
      <button
        type="button"
        className="bf-ref-pill"
        onClick={onOpenChapterSelector}
        aria-label={chapter ? `${bookName} ${chapter}` : bookName}
      >
        <span className="bf-ref-pill__book">{bookName}</span>
        {chapter ? (
          <>
            <span className="bf-ref-pill__sep">·</span>
            <span className="bf-ref-pill__num">{chapter}</span>
          </>
        ) : null}
        <span className="bf-ref-pill__chev" aria-hidden="true">
          <BfIconChevDown />
        </span>
      </button>

      {translationSlot && (
        <span className="bf-header__tr-slot">{translationSlot}</span>
      )}

      {langSlot && <span className="bf-header__lang-slot">{langSlot}</span>}

      <span className="bf-header__spacer" aria-hidden="true" />

      {onOpenSearch && (
        <button type="button" className="bf-icon-btn" onClick={onOpenSearch} aria-label={t('search')}>
          <BfIconSearch />
        </button>
      )}
      {onOpenBookmarks && (
        <button
          type="button"
          className="bf-icon-btn"
          data-state={hasBookmark ? 'on' : 'off'}
          onClick={onOpenBookmarks}
          aria-label={t('bookmarks')}
        >
          <BfIconBookmark />
        </button>
      )}
      {onOpenMenu && (
        <button type="button" className="bf-icon-btn" onClick={onOpenMenu} aria-label={t('menu')}>
          <BfIconMenu />
        </button>
      )}
    </header>
  );
}

const BF_HEADER_BRAND_WIDE_MQ = '(min-width: 640px)';

/**
 * 'tg' — по центру нативной полосы TG (mobile fullscreen).
 * 'center' — по центру шапки BF (только широкий экран: веб desktop, TG desktop).
 */
function useBfHeaderBrandMode() {
  const [mode, setMode] = useState(null);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const mqWide = window.matchMedia(BF_HEADER_BRAND_WIDE_MQ);
    const sync = () => {
      const root = document.documentElement;
      const wide = mqWide.matches;
      const inTg = isTelegramShell();
      const tgDesktop = root.getAttribute('data-tg-desktop') === 'true';
      const tgFullscreen = root.getAttribute('data-tg-fullscreen') === 'true';
      const tgMobile = inTg && !tgDesktop;

      if (!inTg) {
        setMode(wide ? 'center' : null);
        return;
      }

      if (tgMobile && tgFullscreen && !wide) {
        setMode('tg');
        return;
      }

      if (wide && tgDesktop) {
        setMode('center');
        return;
      }

      setMode(null);
    };
    sync();
    mqWide.addEventListener('change', sync);
    const obs = new MutationObserver(sync);
    obs.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-tg-webapp', 'data-tg-desktop', 'data-tg-fullscreen'],
    });
    window.addEventListener('resize', sync);
    return () => {
      mqWide.removeEventListener('change', sync);
      obs.disconnect();
      window.removeEventListener('resize', sync);
    };
  }, []);

  return mode;
}

/**
 * Скрытие верхнего и нижнего BF-chrome по направлению скролла.
 * У самого верха и у самого низа страницы панели снова показываются.
 * Возвращает { hidden, scrolled, show } для <BfHeader /> и <BfChapterPager />.
 *   show() — принудительно показать chrome (без блокировки последующего скролла);
 *           например, при тапе по стиху, открытии inline-меню и т.п.
 */
export function useBfHeaderState({ threshold = 8, bottomThreshold = 96 } = {}) {
  const [hidden, setHidden] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const lastY = useRef(0);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onScroll = () => {
      const y = window.scrollY || window.pageYOffset || 0;
      setScrolled(y > 4);

      const doc = document.documentElement;
      const body = document.body;
      const scrollHeight = Math.max(
        doc.scrollHeight,
        body?.scrollHeight ?? 0,
        doc.offsetHeight,
        body?.offsetHeight ?? 0
      );
      const viewport = window.innerHeight;
      const distanceFromBottom = scrollHeight - (y + viewport);

      if (scrollHeight > viewport && distanceFromBottom <= bottomThreshold) {
        setHidden(false);
        lastY.current = y;
        return;
      }

      const dy = y - lastY.current;
      if (Math.abs(dy) < threshold) return;
      if (y < 80) {
        setHidden(false);
      } else if (dy > 0) {
        setHidden(true);
      } else {
        setHidden(false);
      }
      lastY.current = y;
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [threshold, bottomThreshold]);

  const show = useCallback(() => {
    setHidden(false);
    if (typeof window !== 'undefined') {
      lastY.current = window.scrollY || window.pageYOffset || 0;
    }
  }, []);

  return { hidden, scrolled, show };
}

/**
 * BfReadingProgress — 1px правый рейл с золотым прогрессом главы.
 */
export function BfReadingProgress() {
  const [pct, setPct] = useState(0);
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const update = () => {
      const doc = document.documentElement;
      const max = doc.scrollHeight - doc.clientHeight;
      const y = window.scrollY || window.pageYOffset || 0;
      const next = max > 0 ? Math.min(100, Math.max(0, (y / max) * 100)) : 0;
      setPct(next);
    };
    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, []);
  return (
    <div className="bf-progress-rail" aria-hidden="true">
      <div className="bf-progress-rail__fill" style={{ height: `${pct}%` }} />
    </div>
  );
}
